import React, { useState, useEffect } from "react";
import { IoMdMenu } from "react-icons/io";
import { ImCross } from "react-icons/im";
import {
  HiHome,
  HiUserGroup,
  HiOutlineBriefcase,
  HiOutlinePhone,
} from "react-icons/hi2";

const Navbar = () => {
  const [activeSection, setActiveSection] = useState("Home");
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const navItems = [
    {
      id: "Home",
      label: "Home",
      icon: <HiHome className="text-[18px]" />,
    },
    {
      id: "About",
      label: "About",
      icon: <HiUserGroup className="text-[18px]" />,
    },
    {
      id: "Services",
      label: "Services",
      icon: <HiOutlineBriefcase className="text-[18px]" />,
    },
    {
      id: "Contact",
      label: "Contact",
      icon: <HiOutlinePhone className="text-[18px]" />,
    },
  ];

  const toggleDrawer = () => {
    setIsOpen(!isOpen);
  };

  const scrollToSection = (id) => {
    const section = document.getElementById(id);

    if (section) {
      const navbarHeight = 90;

      const sectionPosition =
        section.getBoundingClientRect().top +
        window.pageYOffset -
        navbarHeight;

      window.scrollTo({
        top: sectionPosition,
        behavior: "smooth",
      });

      setActiveSection(id);

      setIsOpen(false);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);

      const sections = document.querySelectorAll("section[id]");

      let current = "Home";

      sections.forEach((section) => {
        const sectionTop = section.offsetTop - 150;

        if (window.scrollY >= sectionTop) {
          current = section.getAttribute("id");
        }
      });

      setActiveSection(current);
    };

    window.addEventListener("scroll", handleScroll);

    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <>
      <header
        className={`sticky top-0 z-[999] transition-all duration-300 ${
          isScrolled
            ? "bg-white/85 backdrop-blur-xl shadow-lg shadow-black/5 border-b border-primary/5"
            : "bg-white"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-[5%]">
          <div className="flex items-center justify-between h-[82px]">

            {/* LOGO */}
            <div
              onClick={() => scrollToSection("Home")}
              className="cursor-pointer"
            >
              <img
                src={require("../../assets/logo.png")}
                alt="logo"
                className="h-[50px] lg:h-[60px] object-contain"
              />
            </div>

            {/* DESKTOP NAV */}
            <div className="hidden lg:flex items-center gap-3">

              <div className="flex items-center bg-secondary/40 border border-primary/5 rounded-full p-2 shadow-sm">
                {navItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`relative flex items-center gap-2 px-5 py-3 rounded-full text-[0.92vw] font-[600] transition-all duration-300 ${
                      activeSection === item.id
                        ? "bg-white text-primary shadow-md"
                        : "text-text1 hover:text-primary hover:bg-white/70"
                    }`}
                  >
                    <span
                      className={`transition-all duration-300 ${
                        activeSection === item.id
                          ? "text-primary"
                          : "text-text1/90"
                      }`}
                    >
                      {item.icon}
                    </span>

                    {item.label}

                    {activeSection === item.id && (
                      <span className="absolute -bottom-[3px] left-1/2 -translate-x-1/2 w-[22px] h-[3px] rounded-full bg-gradient-to-r from-primary to-gradient2"></span>
                    )}
                  </button>
                ))}
              </div>

              {/* CTA */}
              <button className="ml-2 rounded-full px-7 py-3 bg-gradient-to-r from-primary via-primary to-gradient2 text-white font-semibold shadow-xl shadow-primary/20 hover:scale-[1.03] hover:shadow-primary/30 transition-all duration-300">
                Get Started
              </button>
            </div>

            {/* MOBILE BUTTON */}
            <button
              onClick={toggleDrawer}
              className="lg:hidden w-[44px] h-[44px] rounded-2xl bg-gradient-to-br from-primary/10 to-gradient2/10 flex items-center justify-center text-primary border border-primary/10 shadow-sm"
            >
              {isOpen ? (
                <ImCross className="text-[16px]" />
              ) : (
                <IoMdMenu className="text-[30px]" />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* MOBILE DRAWER */}
      <div
        className={`fixed top-0 right-0 h-full w-[80%] max-w-[340px] bg-white z-[1000] transition-all duration-300 shadow-2xl ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full px-6 py-6">

          {/* TOP */}
          <div className="flex items-center justify-between border-b border-gray-100 pb-5">
            <img
              src={require("../../assets/logo.png")}
              alt="logo"
              className="h-[42px]"
            />

            <button
              onClick={toggleDrawer}
              className="w-[38px] h-[38px] rounded-xl bg-red-50 flex items-center justify-center"
            >
              <ImCross className="text-red-500 text-[15px]" />
            </button>
          </div>

          {/* MOBILE LINKS */}
          <div className="flex flex-col gap-3 mt-8">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`flex items-center gap-4 text-left px-5 py-4 rounded-2xl text-[15px] font-[600] transition-all duration-300 ${
                  activeSection === item.id
                    ? "bg-gradient-to-r from-primary to-gradient2 text-white shadow-lg shadow-primary/20"
                    : "text-text1 bg-gray-50 hover:bg-primary/10 hover:text-primary"
                }`}
              >
                <span className="text-[20px]">
                  {item.icon}
                </span>

                {item.label}
              </button>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-auto pt-8">
            <button className="w-full rounded-2xl py-4 bg-gradient-to-r from-primary to-gradient2 text-white font-semibold shadow-xl shadow-primary/20">
              Get Started
            </button>
          </div>
        </div>
      </div>

      {/* OVERLAY */}
      {isOpen && (
        <div
          onClick={toggleDrawer}
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[999] lg:hidden"
        />
      )}
    </>
  );
};

export default Navbar;